import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import Autoplay from "embla-carousel-autoplay";
import { usePeople, type Person } from "@/hooks/usePeople";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ACBackground } from "@/components/ACBackground";
import { compareByLastName } from "@/lib/utils";
import { SponsoredBy } from "@/components/SponsoredBy";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Matter Lab Satellite School — Alán 50 Symposium" },
      {
        name: "description",
        content:
          "The Matter Lab Satellite School features two hands-on learning tracks: Self-Driving Labs and AI Agents for Scientific Applications. Each track combines a theory session with a practical workshop, providing participants with both conceptual foundations and hands-on experience.",
      },
      {
        property: "og:title",
        content: "Matter Lab Satellite School — Alán 50 Symposium",
      },
      {
        property: "og:description",
        content:
          "Join the Matter Lab Satellite School at UNAM for interactive sessions on Self-Driving Labs and AI Agents for Scientific Applications, combining theory with hands-on practice.",
      },
    ],
  }),
  component: SatelliteSchool,
});
function SatelliteSchool() {
  const { people: tutors } = usePeople("tutors", "tutor-photos");
  const { people: accelerationConsortiumTutors } = usePeople(
    "acceleration_consortium_tutors",
    "acceleration-consortium-tutor-photos",
  );
  const { people: organizers } = usePeople("local_organizers", "organizer-photos");
  const { people: matterLab } = usePeople("matter_lab_organizers", "matter-lab-photos");

  return (
    <main className="relative pt-32 pb-24">
      <ACBackground />
      <div className="relative z-10 mx-auto max-w-5xl px-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
          Matter Lab International Conference 2026
        </div>
        <h1 className="font-display text-4xl sm:text-6xl tracking-tight text-foreground">Satellite School</h1>
        <p className="mt-6 text-lg text-muted-foreground">
          Artificial intelligence is transforming the way scientific discoveries are made. This one-day hands-on
          satellite school, organized by the{" "}
          <a href="https://www.matter.toronto.edu" className="font-bold text-magenta-soft hover:text-foreground">
            Matter Lab
          </a>
          , the{" "}
          <a href="https://acceleration.utoronto.ca" className="font-bold text-primary-soft hover:text-foreground">
            Acceleration Consortium
          </a>
          , and{" "}
          <a href="https://www.c3.unam.mx" className="font-bold text-magenta-soft hover:text-foreground">
            Centro de Ciencias de la Complejidad
          </a>
          , introduces participants to two emerging paradigms at the forefront of AI for Science: Self-Driving Labs and
          AI Agents for Scientific Applications. Through a combination of theory lectures and interactive practical
          sessions, participants will learn how autonomous experimentation, machine learning, and AI agents are
          accelerating research across chemistry, materials science, and related fields. The school is designed for
          students and early-career researchers interested in gaining practical experience with the tools and concepts
          that are shaping the future of scientific discovery. Hands-on sessions will be led by Matter Lab graduate
          students and postdoctoral researchers, providing participants with the opportunity to engage directly with
          state-of-the-art AI workflows.
        </p>

        <div className="mt-16 grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Dates</div>
            <div className="mt-2 text-foreground">August 26, 2026</div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Location</div>
            <div className="mt-2 text-foreground">Centro de Ciencias de la Complejidad</div>
          </div>
          <Link
            to="/schedule"
            className="rounded-2xl border border-border bg-card p-6 block transition-colors hover:bg-card/80 group"
          >
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Agenda</div>
            <div className="mt-2 text-foreground group-hover:text-[oklch(var(--accent))] transition-colors">
              Artificial Intelligence for Science · view schedule →
            </div>
          </Link>
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSfAbNlXRFtve44052jT7dTjc2HWEBLzO-fABwUXpLYorXAHIg/viewform"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-2xl border border-border bg-card p-6 block transition-colors hover:bg-card/80 group"
          >
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Apply</div>
            <div className="mt-2 text-foreground group-hover:text-[oklch(var(--accent))] transition-colors">
              Open application form →
            </div>
          </a>
        </div>

        <PeopleGrid title="Meet the tutors" eyebrow="Tutors" people={tutors} />
        <PeopleGrid
          title="Acceleration Consortium tutors"
          eyebrow="Acceleration Consortium"
          people={accelerationConsortiumTutors}
        />
        {(organizers.length > 0 || matterLab.length > 0) && (
          <PeopleCarousel
            title="Organizers"
            eyebrow="Satellite School"
            people={[...matterLab, ...organizers]}
            perView={3}
          />
        )}
        <SponsoredBy />
      </div>
    </main>
  );
}

function PhotoTile({
  urls,
  activeIdx,
  onCycle,
  fallbackIndex,
}: {
  urls: string[] | undefined;
  activeIdx: number;
  onCycle: () => void;
  fallbackIndex: number;
}) {
  const total = urls?.length ?? 0;
  const current = total > 0 ? urls![activeIdx % total] : undefined;
  const fallbackBg =
    fallbackIndex % 3 === 0
      ? "var(--gradient-aurora)"
      : fallbackIndex % 3 === 1
        ? "var(--gradient-sun)"
        : "linear-gradient(135deg, oklch(var(--cyan)), oklch(var(--magenta)))";

  useEffect(() => {
    if (total <= 1) return;
    const id = setInterval(() => {
      onCycle();
    }, 2000);
    return () => clearInterval(id);
  }, [total, onCycle]);

  return (
    <div
      className="relative aspect-square rounded-xl mb-6 overflow-hidden"
      style={
        current
          ? { backgroundImage: `url(${current})`, backgroundSize: "cover", backgroundPosition: "center" }
          : { background: fallbackBg }
      }
    >
      {total > 1 && (
        <>
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              onCycle();
            }}
            className="absolute inset-0 w-full h-full cursor-pointer"
            aria-label="Next photo"
          />
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 pointer-events-none">
            {urls!.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full ${i === activeIdx % total ? "bg-white" : "bg-white/50"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function DialogPhotoGallery({ urls, alt }: { urls: string[]; alt: string }) {
  const [idx, setIdx] = useState(0);
  const total = urls.length;
  if (total === 0) return null;
  return (
    <div className="shrink-0">
      <img src={urls[idx % total]} alt={alt} className="w-32 h-32 rounded-xl object-cover" />
      {total > 1 && (
        <div className="mt-2 flex items-center justify-center gap-1.5">
          {urls.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              className={`w-2 h-2 rounded-full transition-colors ${i === idx ? "bg-foreground" : "bg-muted-foreground/40 hover:bg-muted-foreground"}`}
              aria-label={`Show photo ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PeopleGrid({ title, eyebrow, people }: { title: string; eyebrow: string; people: Person[] }) {
  const [selected, setSelected] = useState<Person | null>(null);
  const [activeIdx, setActiveIdx] = useState<Record<string, number>>({});
  if (people.length === 0) return null;

  const cycle = (id: string, total: number) => {
    setActiveIdx((prev) => ({ ...prev, [id]: ((prev[id] ?? 0) + 1) % total }));
  };

  return (
    <div className="mt-20">
      <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">{eyebrow}</div>
      <h2
        className="font-display text-3xl sm:text-4xl tracking-tight text-foreground mb-10"
        style={{ fontFamily: "Fraunces, serif" }}
      >
        {title}
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border rounded-2xl overflow-hidden">
        {[...people].sort(compareByLastName).map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelected(p)}
              className="bg-card p-8 text-left hover:bg-foreground/5 transition-colors group flex flex-col"
            >
              <PhotoTile
                urls={p.photos}
                fallbackIndex={i}
                activeIdx={activeIdx[p.id] ?? 0}
                onCycle={() => cycle(p.id, (p.photos ?? []).length)}
              />
              <h3
                className="font-display text-2xl tracking-tight group-hover:text-[oklch(var(--magenta))] transition-colors"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                {p.name.trim()}
              </h3>
              {p.affiliation && <p className="mt-2 text-sm text-muted-foreground">{p.affiliation}</p>}
            </button>
          ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          {selected && (
            <div className="flex gap-6 items-start">
              {selected.photos && selected.photos.length > 0 && (
                <DialogPhotoGallery urls={selected.photos} alt={selected.name.trim()} />
              )}
              <DialogHeader>
                <DialogTitle className="font-display text-3xl" style={{ fontFamily: "Fraunces, serif" }}>
                  {selected.name.trim()}
                </DialogTitle>
                {selected.affiliation && (
                  <DialogDescription className="text-base">{selected.affiliation}</DialogDescription>
                )}
              </DialogHeader>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PeopleCarousel({
  title,
  eyebrow,
  people,
  perView = 2,
}: {
  title: string;
  eyebrow: string;
  people: Person[];
  perView?: number;
}) {
  if (people.length === 0) return null;
  const basis = perView === 2 ? "basis-full sm:basis-1/2" : "basis-full sm:basis-1/2 md:basis-1/3";
  return (
    <div className="mt-20">
      <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">{eyebrow}</div>
      <h2
        className="font-display text-3xl sm:text-4xl tracking-tight text-foreground mb-10"
        style={{ fontFamily: "Fraunces, serif" }}
      >
        {title}
      </h2>
      <Carousel
        opts={{ align: "start", loop: people.length > perView }}
        plugins={useMemo(() => [Autoplay({ delay: 2500, stopOnInteraction: false })], [])}
        className="w-full"
      >
        <CarouselContent className="-ml-4">
          {[...people].sort(compareByLastName).map((p) => (
            <CarouselItem key={p.id} className={`pl-4 ${basis}`}>
              <div className="flex flex-col items-start">
                {p.photos && p.photos.length > 0 ? (
                  <img
                    src={p.photos[0]}
                    alt={p.name}
                    className="w-full aspect-square object-cover rounded-2xl border border-border"
                  />
                ) : (
                  <div className="w-full aspect-square rounded-2xl border border-border bg-card" />
                )}
                <div className="mt-4 w-full">
                  <p className="font-display text-xl text-foreground" style={{ fontFamily: "Fraunces, serif" }}>
                    {p.name}
                  </p>
                  {p.affiliation && <p className="text-sm text-muted-foreground mt-1">{p.affiliation}</p>}
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious />
        <CarouselNext />
      </Carousel>
    </div>
  );
}
